import { useEffect } from "react"
import { useRouter } from "next/router"

import { useBillingSubscription } from "./useBillingSubscription"

const SIGNUP_WELCOME_PATH = "/signup-welcome"

/**
 * Redirects unfinished signups (no subscription row, or status "incomplete")
 * into the funnel from anywhere in the authed app; lapsed self-serve users are
 * left alone. Returns true while a redirect is pending so the caller can hold
 * rendering. Fails open while the check is loading or errors — we never gate
 * the whole app render on the billing API's availability.
 */
export function useSignupCompletionRedirect(): boolean {
  const router = useRouter()
  const { data: subscription, isLoading, error } = useBillingSubscription()

  // Only a missing subscription row (SUBSCRIPTION_NOT_FOUND) is an unfinished
  // signup. A 404 ORGANIZATION_NOT_FOUND (non-member slug / mid org-switch) must
  // not bounce an entitled user — LogInRequired's org-recovery handles that.
  const noSubscriptionRow =
    error?.status === 404 && error.errorCode === "SUBSCRIPTION_NOT_FOUND"
  const unfinishedSignup =
    !isLoading && (noSubscriptionRow || subscription?.status === "incomplete")
  const shouldRedirect =
    unfinishedSignup && router.pathname !== SIGNUP_WELCOME_PATH

  useEffect(() => {
    if (shouldRedirect) {
      void router.replace(SIGNUP_WELCOME_PATH)
    }
  }, [shouldRedirect, router])

  return shouldRedirect
}
